import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import { BackdropCircular, Breadcrumb } from 'components';
import {
  getQuoteProGroupsAttributeApi,
  getQuoteProProductsApi,
  getQuoteProProductsBySearchCriteriaApi
} from 'api';
import { createAutoQuoteApi, getAutoQuotesApi } from 'api/automatedQuotes';
import { calculateTotals, dayRanges } from '../helpers';
import QuoteAutomationItemsTable from './QuoteAutomationItemsTable';
import { QuoteAutomationValidationSchema } from './QuoteAutomationValidationSchema';


const QuoteAutomation = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<{ [id: string]: string }>();
  const [total, setTotal] = useState(0);

  const {
    autoQuoteItems,
    autoQuotes,
    autoQuotesLoading,
    createAutoQuoteLoading
  } = useSelector((state: IReduxState) => state.automatedQuotes);

  const {
    quoteGroupsAttributeData,
    quoteProductsByGroup,
    quoteProducts,
    quoteGroupsLoading,
    quoteProductsLoading,
    loadingQuoteProductSubscriptionOptions,
    allQuoteProducts
  } = useSelector((state: IReduxState) => state.quote);

  const loading =
    quoteGroupsLoading ||
    quoteProductsLoading ||
    loadingQuoteProductSubscriptionOptions ||
    autoQuotesLoading ||
    createAutoQuoteLoading;

  useEffect(() => {
    if (!quoteGroupsAttributeData.options.length) {
      getQuoteProGroupsAttributeApi({ dispatch });
    }

    if (!allQuoteProducts.length) {
      getQuoteProProductsApi({ dispatch });
    }

    if (!Object.keys(quoteProductsByGroup).length || !quoteProducts.length) {
      getQuoteProProductsBySearchCriteriaApi({
        dispatch,
        sortDirection: 'asc',
        sortField: 'name',
        pageSize: '',
        currentPage: '',
        filterConditions: [
          {
            field: 'status',
            value: '1',
            conditionType: 'eq'
          },
        ],
        searchConditions: []
      });
    }

    if (!autoQuotes?.length) {
      getAutoQuotesApi({ dispatch });
    }
  }, []);


  useEffect(() => {
    if (!autoQuoteItems?.length) {
      history.push(`/automated-quotes/${id}/customer-preview/workspace`);
    }

    if (autoQuoteItems?.length) {
      const { totals } = calculateTotals(autoQuoteItems);
      setTotal(totals.total);
    }
  }, [id]);

  const handleSaveAutomation = (values) => {
    const {
      customCronExpression,
      period,
      startAfterDays,
      customStartAfterDays,
      expireAfterDays,
      customExpireAfterDays,
      mustUsedBeforeDays,
      customMustUsedBeforeDays,
      ...restValues
    } = values;

    const automatedQuote = {
      ...restValues,
      period: period === 'custom' && customCronExpression ? customCronExpression : period,
      startAfterDays: startAfterDays === 0 ? customStartAfterDays : startAfterDays,
      expireAfterDays: expireAfterDays === 0 && customExpireAfterDays ? customExpireAfterDays : expireAfterDays,
      mustUsedBeforeDays: mustUsedBeforeDays === 0 ? customMustUsedBeforeDays : mustUsedBeforeDays,
      selectedProducts: autoQuoteItems
    };

    const isQuoteCreated = createAutoQuoteApi({ dispatch, automatedQuote });

    if (isQuoteCreated) {
      history.push('/automated-quotes/schedule');
    }
  };

  const selectedQuoteList = autoQuotes?.find((list) => list.id.toString() == id);

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y pb-25">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Automated Quotes', path: '/automated-quotes' },
            { name: 'Customer Preview', path: `/automated-quotes/${id}/customer-preview` },
            { name: 'Quote Workspace', path: `/automated-quotes/${id}/customer-preview/workspace` },
            { name: 'Quote Automation' }
          ]}
        />
      </div>
      <Card className="mx-10 p-8 root-card">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {!!autoQuoteItems?.length && <QuoteAutomationItemsTable />}
          </Grid>

          <Grid item xs={12}>
            <Formik
              initialValues={{
                quoteName: selectedQuoteList?.name || '',
                startAfterDays: null,
                customStartAfterDays: null,
                expireAfterDays: null,
                customExpireAfterDays: null,
                mustUsedBeforeDays: null,
                customMustUsedBeforeDays: null,
                patientNotice: '',
                quoteDiscount: {
                  price_type: '',
                  price: 0
                },
                period: '',
                customCronExpression: '',
                campaign: {
                  id: ''
                },
                runImmediately: false,
                akwaRuleId: selectedQuoteList?.id || ''
              }}
              validationSchema={QuoteAutomationValidationSchema(total)}
              onSubmit={(values) => handleSaveAutomation(values)}
            >
              {({
                values: {
                  quoteName,
                  startAfterDays,
                  customStartAfterDays,
                  expireAfterDays,
                  customExpireAfterDays,
                  mustUsedBeforeDays,
                  customMustUsedBeforeDays,
                  patientNotice,
                  quoteDiscount: { price, price_type },
                  period,
                  customCronExpression,
                  runImmediately
                },
                errors,
                touched,
                handleBlur,
                handleSubmit,
                setFieldValue,
                handleChange
              }) => (
                <form autoComplete="off" onSubmit={handleSubmit} noValidate className="w-full">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        name="quoteName"
                        label="Quote Name"
                        variant="outlined"
                        value={quoteName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.quoteName && Boolean(errors.quoteName)}
                        helperText={touched.quoteName && errors.quoteName}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        name="startAfterDays"
                        select
                        label="Start Date Range"
                        variant="outlined"
                        value={startAfterDays}
                        onChange={(e) => {
                          setFieldValue('startAfterDays', e.target.value);
                          setFieldValue('customStartAfterDays', null);
                        }}
                        onBlur={handleBlur}
                        error={touched.startAfterDays && Boolean(errors.startAfterDays)}
                        helperText={touched.startAfterDays && errors.startAfterDays}
                      >
                        {dayRanges.map((range) => (
                          <MenuItem key={range.value} value={range.value}>
                            {range.label}
                          </MenuItem>
                        ))}
                        <MenuItem value={0}>Custom</MenuItem>
                        <MenuItem value={null}>Not Selected</MenuItem>
                      </TextField>

                      {startAfterDays === 0 && (
                        <TextField
                          name="customStartAfterDays"
                          label="Custom Start Date Range (days)"
                          fullWidth
                          type="number"
                          variant="outlined"
                          className="mt-2"
                          value={customStartAfterDays}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.customStartAfterDays && Boolean(errors.customStartAfterDays)}
                          helperText={touched.customStartAfterDays && errors.customStartAfterDays}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        name="expireAfterDays"
                        select
                        required
                        label="Expiration Date Range"
                        variant="outlined"
                        value={expireAfterDays}
                        onChange={(e) => {
                          setFieldValue('expireAfterDays', e.target.value);
                          setFieldValue('customExpireAfterDays', null);
                        }}
                        onBlur={handleBlur}
                        error={touched.expireAfterDays && Boolean(errors.expireAfterDays)}
                        helperText={touched.expireAfterDays && errors.expireAfterDays}
                      >
                        {dayRanges.map((range) => (
                          <MenuItem key={range.value} value={range.value}>
                            {range.label}
                          </MenuItem>
                        ))}
                        <MenuItem value={0}>Custom</MenuItem>
                      </TextField>

                      {expireAfterDays === 0 && (
                        <TextField
                          name="customExpireAfterDays"
                          label="Custom Expiration Date Range (days)"
                          fullWidth
                          type="number"
                          variant="outlined"
                          className="mt-2"
                          value={customExpireAfterDays}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.customExpireAfterDays && Boolean(errors.customExpireAfterDays)}
                          helperText={touched.customExpireAfterDays && errors.customExpireAfterDays}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        name="mustUsedBeforeDays"
                        select
                        label="Must Used Date Range"
                        variant="outlined"
                        value={mustUsedBeforeDays}
                        onChange={(e) => {
                          setFieldValue('mustUsedBeforeDays', e.target.value);
                          setFieldValue('customMustUsedBeforeDays', null);
                        }}
                        onBlur={handleBlur}
                        error={touched.mustUsedBeforeDays && Boolean(errors.mustUsedBeforeDays)}
                        helperText={touched.mustUsedBeforeDays && errors.mustUsedBeforeDays}
                      >
                        {dayRanges.map((range) => (
                          <MenuItem key={range.value} value={range.value}>
                            {range.label}
                          </MenuItem>
                        ))}
                        <MenuItem value={0}>Custom</MenuItem>
                        <MenuItem value={null}>Not Selected</MenuItem>
                      </TextField>

                      {mustUsedBeforeDays === 0 && (
                        <TextField
                          name="customMustUsedBeforeDays"
                          label="Custom Must Used Date Range (days)"
                          fullWidth
                          type="number"
                          variant="outlined"
                          className="mt-2"
                          value={customMustUsedBeforeDays}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.customMustUsedBeforeDays && Boolean(errors.customMustUsedBeforeDays)}
                          helperText={touched.customMustUsedBeforeDays && errors.customMustUsedBeforeDays}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box className="w-full">
                        <TextField
                          label="Discount Type"
                          select
                          variant="outlined"
                          name="quoteDiscount.price_type"
                          fullWidth
                          value={price_type}
                          onChange={(event) => {
                            const selectedValue = event.target.value;
                            const newPrice = selectedValue === '' ? 0 : price;
                            setFieldValue('quoteDiscount.price_type', selectedValue);
                            setFieldValue('quoteDiscount.price', newPrice);
                          }}
                          onBlur={handleBlur}
                          error={
                            (touched as {
                              quoteDiscount?: { price_type?: boolean };
                            })?.quoteDiscount?.price_type &&
                            Boolean(
                              (errors as {
                                quoteDiscount?: { price_type?: string };
                              })?.quoteDiscount?.price_type
                            )
                          }
                          helperText={
                            (touched as {
                              quoteDiscount?: { price_type?: boolean };
                            })?.quoteDiscount?.price_type &&
                            (errors as {
                              quoteDiscount?: { price_type?: string };
                            })?.quoteDiscount?.price_type
                          }
                        >
                          <MenuItem value="percent">Percentage</MenuItem>
                          <MenuItem value="fixed">Fixed</MenuItem>
                          <MenuItem value="">Not selected</MenuItem>
                        </TextField>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box className="w-full">
                        <NumberFormat
                          required={!!price_type}
                          customInput={TextField}
                          variant="outlined"
                          name="quoteDiscount.price"
                          fullWidth
                          label="Discount Value"
                          value={price}
                          onValueChange={(values) => {
                            const { floatValue } = values;
                            handleChange({
                              target: {
                                name: 'quoteDiscount.price',
                                value: floatValue !== undefined ? floatValue : ''
                              },
                            });
                          }}
                          onBlur={handleBlur}
                          disabled={!price_type}
                          suffix={price_type === 'percent' ? '%' : ''}
                          prefix={price_type === 'fixed' ? '$' : ''}
                          fixedDecimalScale
                          decimalScale={2}
                          error={(touched as { quoteDiscount?: { price?: boolean }})?.quoteDiscount?.price &&
                            Boolean(
                              (errors as {
                                quoteDiscount?: { price?: string };
                              })?.quoteDiscount?.price
                            )
                          }
                          helperText={
                            (touched as {
                              quoteDiscount?: { price?: boolean };
                            })?.quoteDiscount?.price &&
                            (errors as {
                              quoteDiscount?: { price?: string };
                            })?.quoteDiscount?.price
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        required
                        name="period"
                        select
                        label="How often to run"
                        variant="outlined"
                        value={period || ''}
                        onChange={(e) => {
                          setFieldValue('period', e.target.value);
                          setFieldValue('customCronExpression', '');
                        }}
                        onBlur={handleBlur}
                        error={touched.period && Boolean(errors.period)}
                        helperText={touched.period && errors.period}
                      >
                        <MenuItem value="0 0 * * *">Daily</MenuItem>
                        <MenuItem value="0 0 * * 0">Weekly</MenuItem>
                        <MenuItem value="0 0 1 * *">Monthly</MenuItem>
                        <MenuItem value="0 0 1 1 *">Yearly</MenuItem>
                        <MenuItem value="0 0 * * 1-5">Every Weekday</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                        <MenuItem value="">Not Selected</MenuItem>
                      </TextField>
                    </Grid>

                    {period === 'custom' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required={period === 'custom'}
                          name="customCronExpression"
                          label="Custom Cron Expression"
                          variant="outlined"
                          value={customCronExpression || ''}
                          onChange={(e) => setFieldValue('customCronExpression', e.target.value)}
                          onBlur={handleBlur}
                          error={touched.customCronExpression && Boolean(errors.customCronExpression)}
                          helperText={touched.customCronExpression && errors.customCronExpression}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="runImmediately"
                            color="primary"
                            size="small"
                            checked={runImmediately}
                            onChange={handleChange}
                          />
                        }
                        label="Run Immediately"
                      />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <Box className="w-full">
                        <TextField
                          label="Selected Rule"
                          variant="outlined"
                          name="akwaRuleId"
                          disabled
                          fullWidth
                          value={selectedQuoteList?.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.akwaRuleId && Boolean(errors.akwaRuleId)}
                          helperText={touched.akwaRuleId && errors.akwaRuleId}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="patientNotice"
                        label="Notes"
                        multiline
                        variant="outlined"
                        value={patientNotice}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.patientNotice && Boolean(errors.patientNotice)}
                        helperText={touched.patientNotice && errors.patientNotice}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box className="flex justify-between items-center">
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          className="mr-8"
                        >
                          Save Automation
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Card>

      <BackdropCircular loading={loading} />
    </div>
  );
};

export default QuoteAutomation;
